import { Injectable } from '@jack-henry/frontend-utils/di';
import {
    ApplicationNameTypeDto,
    ArpFileErrorDto,
    ArpParseResultsModelDto,
    IssuedItemFileClient,
    IssuedItemFileCreatedByUserDto,
    IssuedItemFileDto,
    IssuedItemFileErrorModelDto,
    PositivePayCompanyConfigurationClient,
} from '@treasury/api/channel';
import { Nullable } from '@treasury/utils';
import { FiDate } from '../../../dates';
import {
    createIssuedItemsFile,
    IssuedItemsFile,
} from '../../mappings/arp/issued-items-file.entity';
import {
    IssuedItemFileMappings,
    IssuedItemFileSearchParams,
    SourceItem,
} from '../../mappings/arp/issued-items-file.mappings';
import { DownloadService } from '../download';

export type { IssuedItemFileSearchParams, SourceItem };

export class FileProcessingError extends Error {
    constructor(
        message: string,
        public readonly uniqueId: string,
        public readonly fileErrors: ArpFileErrorDto[],
        public readonly requiresHardUpdate: boolean = true
    ) {
        super(message);
        this.name = 'FileProcessingError';
    }
}

export interface IssuedItemFileSearchResult {
    items: IssuedItemsFile[];
    totalCount: number;
    pageSize: number;
    pageNumber: number;
}

@Injectable()
export class IssuedItemFileService {
    constructor(
        private readonly issuedItemFileClient: IssuedItemFileClient,
        private readonly positivePayCompanyConfigurationClient: PositivePayCompanyConfigurationClient
    ) {}

    public async searchIssuedItemFiles(
        searchParams: IssuedItemFileSearchParams = {},
        pageSize = 10,
        pageNumber = 1
    ): Promise<IssuedItemFileSearchResult> {
        const searchRequest = {
            applicationNameType: ApplicationNameTypeDto.Banno,
            pagingParameters: {
                pageSize,
                pageNumber,
                searchBys: IssuedItemFileMappings.mapSearchParams(searchParams),
            },
        };

        const response =
            await this.issuedItemFileClient.issuedItemFileSearchIssuedItemFilesActivity(
                searchRequest
            );

        const items = (response.data?.items || []).map(dto =>
            createIssuedItemsFile(dto as Nullable<IssuedItemFileDto>)
        );

        return {
            items,
            totalCount: response.data?.totalCount || 0,
            pageSize: response.data?.pageSize || pageSize,
            pageNumber: response.data?.pageNumber || pageNumber,
        };
    }

    public async getFileContents(uniqueId: string): Promise<string> {
        const response =
            await this.issuedItemFileClient.issuedItemFileGetIssuedItemFileContent(uniqueId);
        return response.data?.fileContent || '';
    }

    public async getFileErrors(uniqueId: string): Promise<IssuedItemFileErrorModelDto | undefined> {
        const response =
            await this.issuedItemFileClient.issuedItemFileGetIssuedItemFileError(uniqueId);
        return response.data || undefined;
    }

    public async getCreatedByUsers(): Promise<IssuedItemFileCreatedByUserDto[]> {
        const response = await this.issuedItemFileClient.issuedItemFileGetCreatedByUsers();
        return response.data?.createdByUsers || [];
    }

    public async processFile(uniqueId: string): Promise<ArpParseResultsModelDto> {
        const response =
            await this.issuedItemFileClient.issuedItemFileProcessIssuedItemFile(uniqueId);

        if (response.data && (!response.data.fileErrors || response.data.fileErrors.length === 0)) {
            return response.data as ArpParseResultsModelDto;
        }

        // If there are file errors, throw structured error so that the caller can request the exact detailed errors from the getFileError API
        if (response.data?.fileErrors && response.data.fileErrors.length > 0) {
            throw new FileProcessingError(
                'One or more errors were encountered during validation of issued items within the file. See more...',
                uniqueId,
                response.data.fileErrors
            );
        }

        throw new Error('File processing failed with unknown error');
    }

    public async deleteFile(uniqueId: string): Promise<boolean> {
        const response =
            await this.issuedItemFileClient.issuedItemFileDeleteIssuedItemFile(uniqueId);
        return response.data || false;
    }

    public async downloadFileActivity(
        outputType: 'PDF' | 'CSV',
        searchParams: IssuedItemFileSearchParams = {},
        pageSize = 10,
        pageNumber = 1
    ): Promise<void> {
        const fileSuffix = new FiDate(new Date()).toIsoDate();
        const pageId = 'IssuedItemFileActivity';

        const filterModel = {
            applicationNameType: 2,
            pagingParameters: {
                pageSize,
                pageNumber,
                searchBys: IssuedItemFileMappings.mapSearchParams(searchParams),
            },
        };

        return DownloadService.download(
            fileSuffix,
            outputType,
            pageId,
            { ...filterModel, page: pageId },
            ['CSV', 'PDF']
        );
    }

    public async downloadProcessingErrors(
        uniqueId: string,
        outputType: 'PDF' | 'CSV'
    ): Promise<void> {
        if (!outputType) {
            throw new Error('Output type is required for downloading processing errors');
        }

        const confirmationId = new FiDate(new Date()).toIsoDate();
        const pageId = 'IssuedItemFileProcessErrors';

        const numericOutputType = outputType === 'PDF' ? 1 : 2; // 1 for PDF, 2 for CSV

        const filterModel = {
            page: pageId,
            id: uniqueId,
            outputType: numericOutputType,
        };

        return DownloadService.download(confirmationId, outputType, pageId, filterModel, [
            'CSV',
            'PDF',
        ]);
    }

    public async getCompanyPosPayConfigurationOptions(): Promise<Record<string, boolean>> {
        const response =
            await this.positivePayCompanyConfigurationClient.positivePayCompanyConfigurationGetAllOptions();

        const options: Record<string, boolean> = {};

        if (response.data) {
            response.data.forEach(item => {
                if (item.name && item.value) {
                    const cleanName = item.name.replace(/^PositivePay\./, '');
                    options[cleanName] = item.value === '1';
                }
            });
        }

        return options;
    }
}
